import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import './App.css';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/firebase.js';

// Import pages
import Home from '../Choose_path/intropage.jsx';
import Dashboard from '../Sidebars/sidebar_panel/components/Dashboard.jsx';
import Settings from '../avatarSystem/settings/settings.jsx';
import Podcast from "../podcastSystem/podcast.jsx";
import Community from "../Sidebars/sidebar_panel/components/community.jsx";
import Plan from '../Payment_plan/plan.jsx';
import Test from '../pronunciationSystem/test.jsx';
import Emma from '../avatarSystem/chatSystem/testing/emma.jsx';
import Chatting1 from '../avatarSystem/chatSystem/testing/alex-updated.jsx';
import Chatting from '../avatarSystem/chatSystem/testing/alex.jsx';
import Chat from '../avatarSystem/chatSystem/AvatarChat.jsx';
import ConversationSession from "../avatarSystem/chatSystem/conversation.jsx";
import IntroPage from "../avatarSystem/chatSystem/testing/intropage.jsx";
import JournalPage from "../avatarSystem/chatSystem/testing/JournalPage.jsx";
import AdminDashboard from '../avatarSystem/chatSystem/testing/Admin.jsx';
import NotesPage from '../avatarSystem/chatSystem/testing/Notespage.jsx';
import ChatMode from '../avatarSystem/chatSystem/testing/ChatMode.jsx';
import AppIntro from '../avatarSystem/chatSystem/testing/AppIntro.jsx';
import SharedContentPage from '../avatarSystem/chatSystem/testing/SharedContentPage.jsx';
import LandingPage from '../avatarSystem/chatSystem/testing/LandingPage.jsx';
import Map from '../avatarSystem/chatSystem/testing/Map.jsx';

// ============================================
// 🔥 IMPORT FIRESTORE SERVICE
// ============================================
// Try to import from the correct location
let FirestorePersistentStorageService = null;

try {
  // Try the path from your old App.js
  FirestorePersistentStorageService = require('../avatarSystem/chatSystem/testing/services/FirestorePersistentStorageService.jsx').default;
  console.log('✅ FirestoreStorage service imported successfully');
} catch (e) {
  console.error('❌ Failed to import FirestorePersistentStorageService:', e.message);
  console.error('💡 Update the import path in App.js to match your project structure');
}

// ============================================
// LOADING SCREEN
// ============================================
const LoadingScreen = () => (
  <div className="App">
    <div className="preptune-main-loading-screen">
      <div className="admin-logo-wrapper">
        <img src="/image/logo.png" alt="Preptune Logo" className="admin-logo" />
        <span className="admin-fade-text">Preptune</span>
      </div>
    </div>
  </div>
);

// ============================================
// PROTECTED ROUTE
// ============================================
const ProtectedRoute = ({ user, children }) => {
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// ============================================
// ROUTES COMPONENT
// ============================================
const AppRoutes = ({ user, loading, firestoreService }) => {
  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="App">
      <Routes>
        {/* Login */}
        <Route 
          path="/login" 
          element={user ? <Navigate to="/" replace /> : <AppIntro />} 
        />

        {/* Home */}
        <Route 
          path="/" 
          element={
            <ProtectedRoute user={user}>
              <ChatMode />
            </ProtectedRoute>
          } 
        />

        {/* Shared Content Route (Public - No auth required) */}
        <Route 
          path="/shared/:shareId" 
          element={<SharedContentPage />} 
        />

        {/* All other routes */}
        <Route path="/about-us" element={<LandingPage />} />
        
        {/* Admin Route - Protected + Pass firestoreService */}
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute user={user}>
              <AdminDashboard 
                firestoreStorage={firestoreService}
                callTimerService={null}
              />
            </ProtectedRoute>
          } 
        />

        <Route path="/run" element={<ProtectedRoute user={user}><Home /></ProtectedRoute>} />
        <Route path="/testing" element={<ProtectedRoute user={user}><Chatting /></ProtectedRoute>} />
        <Route path="/voice-mode" element={<ProtectedRoute user={user}><Chatting1 /></ProtectedRoute>} />
        <Route path="/chat/emma" element={<ProtectedRoute user={user}><Emma /></ProtectedRoute>} />
        <Route path="/notes" element={<ProtectedRoute user={user}><NotesPage /></ProtectedRoute>} />
        <Route path="/journal" element={<ProtectedRoute user={user}><JournalPage /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute user={user}><Dashboard /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute user={user}><Settings /></ProtectedRoute>} />
        <Route path="/podcast" element={<ProtectedRoute user={user}><Podcast /></ProtectedRoute>} />
        <Route path="/community" element={<ProtectedRoute user={user}><Community /></ProtectedRoute>} />
        <Route path="/plan" element={<ProtectedRoute user={user}><Plan /></ProtectedRoute>} />
        <Route path="/test" element={<ProtectedRoute user={user}><Test /></ProtectedRoute>} />
        <Route path="/map" element={<ProtectedRoute user={user}><Map /></ProtectedRoute>} />
        <Route path="/conversations" element={<ProtectedRoute user={user}><ConversationSession /></ProtectedRoute>} />

        {/* Catch all */}
        <Route path="*" element={<Navigate to={user ? "/" : "/login"} replace />} />
      </Routes>
    </div>
  );
};

// ============================================
// MAIN APP
// ============================================
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [firestoreService, setFirestoreService] = useState(null);

  // ============================================
  // 🔥 INITIALIZE FIRESTORE SERVICE
  // ============================================
  useEffect(() => {
    const initFirestore = async () => {
      if (!FirestorePersistentStorageService) {
        console.error('❌ FirestorePersistentStorageService not available');
        return;
      }

      try {
        console.log('🚀 Initializing Firestore service...');
        
        const service = new FirestorePersistentStorageService();
        
        // Initialize the service
        if (service.initialize) {
          await service.initialize();
          console.log('✅ Service initialized');
        }
        
        // Store in state
        setFirestoreService(service);
        
        // ✅ EXPOSE GLOBALLY for admin dashboard
        window.firestoreStorage = service;
        window.FirestorePersistentStorageService = FirestorePersistentStorageService;
        
        console.log('✅ Firestore service ready and exposed globally');
        console.log('   - Props service:', !!service);
        console.log('   - Window service:', !!window.firestoreStorage);
        
      } catch (error) {
        console.error('❌ Failed to initialize Firestore:', error);
      }
    };

    initFirestore();
  }, []);

  // ============================================
  // MONITOR AUTH STATE
  // ============================================
  useEffect(() => {
    let isMounted = true;

    console.log('👂 Setting up auth listener...');
    
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!isMounted) return;

      console.log('🔄 Auth state changed:', currentUser?.email || 'None');

      setUser(currentUser);
      setLoading(false);
      setAuthChecked(true);
    }, (error) => {
      console.error('❌ Auth error:', error);
      setLoading(false);
      setAuthChecked(true);
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  // ============================================
  // WAIT FOR AUTH CHECK
  // ============================================
  if (!authChecked) {
    return <LoadingScreen />;
  }

  return (
    <Router>
      <AppRoutes 
        user={user} 
        loading={loading} 
        firestoreService={firestoreService}
      />
    </Router>
  );
}

export default App;